import React, { useEffect, useReducer, useRef } from 'react';
import NumberInput from '../../../../components/bearing-search/components/forms/NumberInput';
import DMSInput from '../../Utils/DMSInput';
import { convertNumberInput } from '../../../utils/validations';
import { angleConversionReducer, angleConversionInitialState, AngleConversionState } from './reducer';
import { useConversion } from '../../ConversionContext';
import { getConvertedUnit } from '../../../../components/bearing-search/api/api';
import { ConversionUnitsPayload } from '../../../../components/bearing-search/types/types';
import { ConversionToolTabProps } from '../../types';
import { mapConversionAnalyicsData } from '../../Utils/AnalyticsUtils';
import { tabInputDebounceFunction } from '../Utils/TabUtils';
import { trackScientificNotationChangeEvent } from "../../../utils/analytics";

const TabAngle: React.FC<ConversionToolTabProps> = ({ description }) => {
	const { exponent, precision } = useConversion();

	const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
	const lastInputRef = useRef<{
		category: 'angle';
		unit: string;
		value: string;
	} | null>(null);

	const lastResetCategoryRef = useRef<'angle' | null>(null);
	// Reducer
	const [ angleConversionState, dispatch ] = useReducer(
		angleConversionReducer,
		angleConversionInitialState,
	);
	const currentStateRef = useRef();

	/**
	 * Recalculate all conversions units
	 */
	const recalculateAllConversions = async () => {
		let changedCategories = [];

		for (const [ categoryKey, units] of Object.entries(angleConversionState)) {
			const category = categoryKey as keyof AngleConversionState;

			for (const [ unit, value] of Object.entries(units)) {
				if (value !== undefined && value !== '' && value !== '0') {
					if (!changedCategories.includes(category)){
						changedCategories.push(category);
					}
					if (!changedCategories.includes('precision exponent') && exponent) {
						changedCategories.push('precision exponent');
					}
					if (!changedCategories.includes('precision') && precision != 4) {
						changedCategories.push('precision');
					}
					callAndDispatchConversion(category, unit, value, true);
				}
			}
		}

		return changedCategories;
	};

	/**
	 * Reset all conversion units
	 */
	const resetAllConversions = () => {
		for (const [ categoryKey, units ] of Object.entries(angleConversionState)) {
			const category = categoryKey as keyof AngleConversionState;

			for (const [ value] of Object.entries(units)) {
				if (value !== undefined && value !== '' && value !== '0') {
					dispatch({
						type: 'reset_category',
						payload: {
							category,
						},
					});
				}
			}
		}
	};

	/**
	 * Update the converted values with the new exponent
	 * convert based on the first input of each category
	 */
	useEffect(() => {
		currentStateRef.current = angleConversionState;
		recalculateAllConversions().then((result) => {
			trackScientificNotationChangeEvent('conversionTool', result);
		});
	}, [ exponent ]);

	/**
	 * Reset the values when precision is changed
	 */
	useEffect(() => {
		resetAllConversions();
	}, [ precision ]);

	// Map of conversion types for API payload
	const conversionTypeMap: Record<string, string[]> = {
		deg: [ 'decimalDegreeToDDMMSS', 'degreeToRadian', 'degreeTomilliradian' ],
		dms: [ 'DDMMSSToDecimalDegree', 'DDMMSSToRadian', 'DDMMSSToMilliradian' ],
		rad: [ 'radianToDDMMSS', 'radiansTomilliradians', 'radianToDegree' ],
		mrad: [ 'milliradianToDDMMSS', 'milliradiansToRadians', 'milliradianToDegree' ],
	};

	// Map of converted units from API response
	const convertedUnitMap: Record<string, string> = {
		decimalDegreeToDDMMSS: 'dms',
		degreeToRadian: 'rad',
		degreeTomilliradian: 'mrad',
		DDMMSSToDecimalDegree: 'deg',
		DDMMSSToRadian: 'rad',
		DDMMSSToMilliradian: 'mrad',
		radianToDDMMSS: 'dms',
		radiansTomilliradians: 'mrad',
		radianToDegree: 'deg',
		milliradianToDDMMSS: 'dms',
		milliradiansToRadians: 'rad',
		milliradianToDegree: 'deg',
	};

	/**
	 * @param  category
	 * @param  unit
	 * @param  value
	 * @param skipAnalyticCall
	 */
	const callAndDispatchConversion = async (
		category: 'angle',
		unit: string,
		value: string,
		skipAnalyticCall: boolean
	) => {
		const conversionTypeArray = conversionTypeMap[ unit ] || [];
		if (conversionTypeArray.length === 0) {
			return;
		}

		const conversionPayload: ConversionUnitsPayload = {
			unitConversionInputs: [
				{
					conversionType: conversionTypeArray,
					inputValue: value,
					precision,
					exponent,
				},
			],
		};

		try {
			const response = await getConvertedUnit(conversionPayload);
			// ANALYTICS
			if (!skipAnalyticCall) {
				mapConversionAnalyicsData(category, conversionPayload, response);
			}

			if (response.unitConversionResults && category !== lastResetCategoryRef.current) {
				for (const conversionResult of response.unitConversionResults) {
					const convertedType = conversionResult.convertedTypes;
					const convertedValue = conversionResult.convertedValue;
					const convertedUnit = convertedUnitMap[ convertedType ];

					if (! convertedUnit) {
						console.log(`${ convertedType } index doesn't exit in convertedUnitMap`);
						return;
					}

					const converted = {
						[ convertedUnit ]: convertedValue.replaceAll(/,/g, ''),
					};

					dispatch({
						type: 'update_unit',
						payload: {
							category,
							updatedUnits: converted,
						},
					});
				}
			} else {
				dispatch({
					type: 'reset_category',
					payload: {
						category,
					},
				});
			}
		} catch (error) {
			console.error('Conversion API failed', error);
		}
	};

	/**
	 * @param  category
	 * @param  unit
	 * @param  value
	 */
	const handleInputChange = (
		category: 'angle',
		unit: string,
		value: string,
	) => {
		const numericValue = convertNumberInput(value);

		if (unit !== 'dms' && numericValue === undefined) {
			dispatch({
				type: 'reset_category',
				payload: { category },
			});

			lastResetCategoryRef.current = category;

			return; // return, don't call API
		}

		dispatch({
			type: 'update_unit',
			payload: {
				category,
				updatedUnits: { [ unit ]: value },
			},
		});

		lastResetCategoryRef.current = null;
		lastInputRef.current = { category, unit, value };

		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
		}

		const debounceDelay = value.length < 2 ? 0 : 600 + (value.length * 100);

		debounceTimeout.current = setTimeout(() => {
			tabInputDebounceFunction(lastInputRef, callAndDispatchConversion);
		}, debounceDelay);
	};

	/**
	 * @param currentValue
	 * @param category
	 * @param unit
	 * @param newValue
	 */
	const handleBlur = (
		currentValue: string,
		category: 'angle',
		unit: string,
		newValue: string,
	) => {
		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
			debounceTimeout.current = null;
		}

		const numericValue = Number.parseFloat(newValue);
		if (Number.isNaN(numericValue) || numericValue === 0) {
			return;
		}

		if (currentValue == newValue) {
			return;
		}

		callAndDispatchConversion(category, unit, newValue, false);
	};

	return (
		<>
			{/* Degree */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Degree</span>
				</div>
				<div className="col-12 col-md-8">
					<div className="row align-items-center mx-md-0">
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-angle-deg"
								label={''}
								unitLabel={'(deg)'}
								value={angleConversionState.angle.deg}
								min={0}
								onChange={(e) => handleInputChange('angle', 'deg', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'angle', 'deg', angleConversionState.angle.deg ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<DMSInput
								id="conversion-angle-dms"
								label={''}
								unitLabel={'D/M/S'}
								value={angleConversionState.angle.dms}
								onChange={(e) => handleInputChange('angle', 'dms', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'angle', 'dms', angleConversionState.angle.dms ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Radian */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Radian</span>
				</div>
				<div className="col-12 col-md-8">
					<div className="row align-items-center mx-md-0">
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-angle-rad"
								label={''}
								unitLabel={'rad'}
								value={angleConversionState.angle.rad}
								min={0}
								onChange={(e) => handleInputChange('angle', 'rad', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'angle', 'rad', angleConversionState.angle.rad ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-angle-mrad"
								label={''}
								unitLabel={'mrad'}
								value={angleConversionState.angle.mrad}
								min={0}
								onChange={(e) => handleInputChange('angle', 'mrad', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'angle', 'mrad', angleConversionState.angle.mrad ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default TabAngle;
